/**
 * Landing page (`/`): one card per registered app with a link to
 * `/app/?app=<name>` and a QR code of the absolute URL, plus the build info.
 *
 * The app list and the build info are injected at build time by `define` in
 * node/config.ts (`__SB_APPS__`, `__SB_COMMIT__`, `__SB_BRANCH__`,
 * `__SB_BUILT_AT__`; see src/vite-env.d.ts). The example apps' descriptions
 * live in node/examples.ts.
 */

import { toString as qrToString } from 'qrcode';

const REPO_URL = 'https://github.com/noya-app/SwiftBrowser';

/** The card's subtitle: the spec's description, else a generic line. */
export function describeApp(app: Pick<SBLandingApp, 'name' | 'description'>): string {
  return app.description ?? `the ${app.name} app`;
}

/** Path of the app page for an example (relative, so links work on any host). */
export function appPath(name: string): string {
  return `/app/?app=${encodeURIComponent(name)}`;
}

/** Absolute URL a phone should open (the QR code's payload). */
export function appUrl(name: string): string {
  return `${location.origin}${appPath(name)}`;
}

function el<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  className?: string,
  text?: string,
): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag);
  if (className) node.className = className;
  if (text !== undefined) node.textContent = text;
  return node;
}

async function renderQr(container: HTMLElement, url: string): Promise<void> {
  try {
    // The SVG is generated from the URL (modules as a path), nothing is
    // interpolated into the markup.
    const svg = await qrToString(url, { type: 'svg', margin: 1, errorCorrectionLevel: 'M' });
    container.innerHTML = svg;
    const node = container.querySelector('svg');
    if (node) {
      node.setAttribute('role', 'img');
      node.setAttribute('aria-label', `QR code for ${url}`);
      node.removeAttribute('width');
      node.removeAttribute('height');
    }
  } catch (error) {
    console.error('[sb] could not render QR code', error);
    container.textContent = url;
    container.classList.add('app-qr--failed');
  }
}

function appCard(app: SBLandingApp): HTMLLIElement {
  const { name, displayName } = app;
  const card = el('li', 'app-card');
  card.dataset['app'] = name;

  const qr = el('div', 'app-qr');
  qr.title = appUrl(name);
  void renderQr(qr, appUrl(name));

  const body = el('div', 'app-body');
  const heading = el('div', 'app-heading');
  if (app.icon) {
    const icon = el('img', 'app-icon');
    icon.src = app.icon;
    icon.alt = '';
    icon.width = 40;
    icon.height = 40;
    heading.append(icon);
  }
  const title = el('h3', 'app-name', displayName);
  heading.append(title);
  const description = el('p', 'app-description', describeApp(app));
  const open = el('a', 'app-open', 'Open');
  open.href = appPath(name);
  open.setAttribute('aria-label', `Open ${displayName}`);
  const url = el('a', 'app-url', appUrl(name).replace(/^https?:\/\//, ''));
  url.href = appPath(name);
  url.setAttribute('aria-hidden', 'true');
  url.tabIndex = -1;

  body.append(heading, description, open, url);
  card.append(qr, body);
  return card;
}

function renderApps(): void {
  const list = document.querySelector<HTMLUListElement>('#apps');
  if (!list) return;
  const apps = Array.from(__SB_APPS__);
  if (apps.length === 0) {
    list.replaceWith(el('p', 'app-empty', 'No apps were registered at build time.'));
    return;
  }
  for (const app of apps) list.append(appCard(app));
}

function renderBuildInfo(): void {
  const footer = document.querySelector<HTMLElement>('#build-info');
  if (!footer) return;
  footer.textContent = '';

  const commit = __SB_COMMIT__;
  const branch = __SB_BRANCH__;
  const built = new Date(__SB_BUILT_AT__);

  footer.append('Built from ');
  const sha = commit === 'dev' ? el('span', 'build-commit', 'dev') : el('a', 'build-commit', commit.slice(0, 7));
  sha.dataset['commit'] = commit;
  if (sha instanceof HTMLAnchorElement) {
    sha.href = `${REPO_URL}/commit/${commit}`;
    sha.title = commit;
  }
  footer.append(sha);
  if (branch) {
    footer.append(' on ');
    footer.append(el('span', 'build-branch', branch));
  }
  if (!Number.isNaN(built.getTime())) {
    footer.append(' · ');
    const time = el('time', 'build-date', built.toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' }));
    time.dateTime = built.toISOString();
    footer.append(time);
  }
  footer.append(' · ');
  const repo = el('a', 'build-repo', 'noya-app/SwiftBrowser');
  repo.href = REPO_URL;
  footer.append(repo);
}

renderApps();
renderBuildInfo();
